import React from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Slider,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ReplayIcon from '@material-ui/icons/Replay'

import { LEVERS, LEVER_COLORS } from '../constants'
import {
    AiRecommendation,
    LeasingSeasonConfig,
    LeverKey,
} from '../types/leasingSeason.types'
import { loriTargetForLever } from '../utils/demoProgress'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        height: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    loriBanner: {
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '10',
        border: `1px solid ${theme.palette.primary.main + '24'}`,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
    },
    loriBannerTitle: {
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.25),
    },
    leverRow: {
        '& + &': {
            marginTop: theme.spacing(2.5),
        },
    },
    leverHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.75),
    },
    leverLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    leverDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
    },
    leverMeta: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    leverMetaStrong: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    loriHint: {
        marginTop: theme.spacing(0.5),
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
    },
    loriDiff: {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
}))

interface TokenAllocationPanelProps {
    config: LeasingSeasonConfig
    recommendation: AiRecommendation
    monthlyTokens?: number | null
    onAllocationChange: (key: LeverKey, value: number) => void
    onReset: () => void
}

export const TokenAllocationPanel: React.FC<TokenAllocationPanelProps> = ({
    config,
    recommendation,
    monthlyTokens,
    onAllocationChange,
    onReset,
}) => {
    const classes = useStyles()

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="subtitle1">
                            <strong>Allocation</strong>
                        </Typography>
                        <Typography className={classes.subtitle}>
                            Move sliders to point tokens at the levers that move
                            your goal. Normalized to 100%. 0% on Prospect data
                            enriching turns enrichment off.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={onReset}
                    >
                        Reset
                    </Button>
                </Box>

                <Box className={classes.loriBanner}>
                    <Typography className={classes.loriBannerTitle}>
                        Lori · {recommendation.title}
                    </Typography>
                    {recommendation.body}
                </Box>

                {LEVERS.map((lever) => {
                    const percent = config.allocations[lever.key]
                    const loriTarget = loriTargetForLever(
                        lever.key,
                        recommendation,
                    )
                    const diff = loriTarget - percent
                    const tokens =
                        monthlyTokens != null
                            ? Math.round((percent / 100) * monthlyTokens)
                            : null

                    return (
                        <Box key={lever.key} className={classes.leverRow}>
                            <Box className={classes.leverHeader}>
                                <Box className={classes.leverLabelRow}>
                                    <span
                                        className={classes.leverDot}
                                        style={{
                                            backgroundColor:
                                                LEVER_COLORS[lever.key],
                                        }}
                                    />
                                    <Typography variant="body2">
                                        <strong>{lever.label}</strong>
                                    </Typography>
                                    {lever.comingSoon && (
                                        <Chip
                                            label="Coming soon"
                                            size="small"
                                        />
                                    )}
                                </Box>
                                <Typography className={classes.leverMeta}>
                                    {tokens != null && (
                                        <>
                                            <span
                                                className={
                                                    classes.leverMetaStrong
                                                }
                                            >
                                                {tokens.toLocaleString()}
                                            </span>
                                            {' · '}
                                        </>
                                    )}
                                    <span className={classes.leverMetaStrong}>
                                        {percent}%
                                    </span>
                                </Typography>
                            </Box>
                            <Slider
                                value={percent}
                                min={0}
                                max={100}
                                step={1}
                                onChange={(_, value) =>
                                    onAllocationChange(
                                        lever.key,
                                        value as number,
                                    )
                                }
                                disabled={lever.comingSoon}
                                color="primary"
                            />
                            <Typography className={classes.loriHint}>
                                Lori recommends{' '}
                                <span className={classes.loriDiff}>
                                    {loriTarget}%
                                </span>
                                {diff !== 0 && (
                                    <>
                                        {' '}
                                        ({diff > 0 ? '+' : ''}
                                        {diff})
                                    </>
                                )}
                            </Typography>
                        </Box>
                    )
                })}
            </CardContent>
        </Card>
    )
}
